import { Controller } from "@hotwired/stimulus"

// The owner's list editor: wishes are rows, and one shared modal edits a wish.
//
// Everything saves as it happens. Tapping a row opens the modal pre-filled from
// the row's data-wish JSON; "Ferdig" / "Legg til" submit the modal's own form,
// which carries exactly one wish (nested attributes) plus a mirror of the title —
// so a brand-new list is created together with its first wish. The server
// redirects back here with ?after=, which we use to pop the new row / scroll to
// the edited one. The title itself saves in the background on change; when that
// creates a brand-new list, both forms adopt the new list's URL so a wish typed
// right after lands on it, and we only visit the proper editor page once no
// modal is open.
//
//   .page{data: {controller: "wish-editor"}}
//     form{data: {wish_editor_target: "titleForm"}}
//       input{data: {wish_editor_target: "title", action: "change->wish-editor#saveTitle"}}
//     .li-card{data: {wish_editor_target: "rows"}}
//       button.li-row{data: {wish_row: true, wish: "{json}", action: "wish-editor#edit"}}
//       button{data: {action: "wish-editor#add"}}
//     p{data: {wish_editor_target: "status"}}
//     dialog{data: {wish_editor_target: "dialog"}} …form{data: {wish_editor_target: "wishForm"}}…
//     dialog{data: {wish_editor_target: "confirmDialog"}}
export default class extends Controller {
  static targets = ["title", "titleForm", "status", "rows",
    "dialog", "wishForm", "heading", "headerMirror", "after", "wishId", "destroy",
    "name", "price", "qty", "qtyDisplay", "url", "photo", "note",
    "deleteButton", "submit", "submitAdd", "submitDone",
    "confirmDialog", "confirmName"]
  static values = { persisted: Boolean, saved: Boolean, justAdded: Boolean, focus: String }

  static DEFAULT_STATUS = "Alt lagres automatisk."

  connect() {
    this.savedTitle = this.hasTitleTarget ? this.titleTarget.value : ""
    this.inFlight = false
    this.removing = false
    this.currentId = null
    this.titleSave = null // the in-flight background save, if any
    this.pendingVisit = null // editor URL to visit once the modal closes
    // Turbo snapshots the page before caching it; an <open> dialog in that
    // snapshot would show up frozen on back/forward.
    this.closeAll = () => {
      this.pendingVisit = null
      if (this.hasDialogTarget && this.dialogTarget.open) this.dialogTarget.close()
      if (this.hasConfirmDialogTarget && this.confirmDialogTarget.open) this.confirmDialogTarget.close()
    }
    document.addEventListener("turbo:before-cache", this.closeAll)
    this.runAfterSave()
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.closeAll)
    if (this.statusTimer) clearTimeout(this.statusTimer)
  }

  // React to the server's ?after= signal once we land back on the editor.
  runAfterSave() {
    if (this.savedValue) this.setStatus("Lagret ✓", { reset: true })
    if (!this.hasRowsTarget) return
    if (this.justAddedValue) {
      const rows = this.rowsTarget.querySelectorAll("[data-wish-row]")
      this.animateRow(rows[rows.length - 1], { scroll: true })
    } else if (/^\d+$/.test(this.focusValue)) {
      const row = this.rowsTarget.querySelector(`#wish_${this.focusValue}`)
      if (row) {
        const smooth = !window.matchMedia("(prefers-reduced-motion: reduce)").matches
        requestAnimationFrame(() => row.scrollIntoView({ behavior: smooth ? "smooth" : "auto", block: "nearest" }))
      }
    }
    if (this.savedValue) this.scrubUrl()
  }

  // Drop ?after= so a reload / back-forward doesn't replay the scroll/animation.
  scrubUrl() {
    if (window.history.replaceState) window.history.replaceState({}, "", window.location.pathname)
  }

  animateRow(row, { scroll = false } = {}) {
    if (!row) return
    row.classList.add("wish-pop")
    setTimeout(() => row.classList.remove("wish-pop"), 750)
    if (!scroll) return
    const smooth = !window.matchMedia("(prefers-reduced-motion: reduce)").matches
    requestAnimationFrame(() => row.scrollIntoView({ behavior: smooth ? "smooth" : "auto", block: "nearest" }))
  }

  // --- title autosave -----------------------------------------------------
  // Enter shouldn't submit the (single-field) form — blur instead, which fires
  // `change` if the title actually changed.
  titleEnter(event) {
    event.preventDefault()
    this.titleTarget.blur()
  }

  saveTitle() {
    const title = this.titleTarget.value.trim()
    if (title === this.savedTitle.trim()) return
    if (!title && !this.persistedValue) return // nothing to create yet
    this.setStatus("Lagrer…")
    this.titleSave = this.persistTitle(title).finally(() => { this.titleSave = null })
  }

  async persistTitle(title) {
    const form = this.titleFormTarget
    const creating = !this.persistedValue
    try {
      const response = await fetch(form.action, {
        method: "POST", // _method=patch rides in the form data for an existing list
        body: new FormData(form),
        headers: { "X-CSRF-Token": this.csrfToken(), Accept: "text/html" },
        credentials: "same-origin"
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      if (creating) this.adopt(response.url)
      this.savedTitle = title
      this.setStatus("Lagret ✓", { reset: true })
    } catch (_error) {
      // Let the full round-trip show the server's message (e.g. a blank title).
      form.requestSubmit()
    }
  }

  // A new list was just created in the background: the server sent us to its
  // editor. Point both forms at the list so the next save updates it instead of
  // creating another, and visit the editor for real — unless a wish is being
  // written in the modal, in which case we do that once it closes (or its own
  // submit takes us there).
  adopt(editorUrl) {
    const url = new URL(editorUrl, window.location.href)
    const listPath = url.pathname.replace(/\/edit\/?$/, "")
    if (!/^\/lists\/\d/.test(listPath)) throw new Error("not the editor")
    this.persistedValue = true
    ;[this.titleFormTarget, this.wishFormTarget].forEach((form) => {
      form.action = listPath
      if (!form.querySelector("input[name='_method']")) {
        const method = document.createElement("input")
        method.type = "hidden"
        method.name = "_method"
        method.value = "patch"
        form.prepend(method)
      }
    })
    if (this.dialogTarget.open) this.pendingVisit = url.href
    else this.visit(url.href)
  }

  visit(href) {
    this.pendingVisit = null
    if (window.Turbo) window.Turbo.visit(href)
    else window.location.assign(href)
  }

  setStatus(text, { reset = false } = {}) {
    if (!this.hasStatusTarget) return
    if (this.statusTimer) clearTimeout(this.statusTimer)
    this.statusTarget.textContent = text
    if (reset) {
      this.statusTimer = setTimeout(() => { this.statusTarget.textContent = this.constructor.DEFAULT_STATUS }, 2000)
    }
  }

  // --- the wish modal -----------------------------------------------------
  add(event) {
    if (event) event.preventDefault()
    this.open(null)
  }

  edit(event) {
    if (event) event.preventDefault()
    const row = event.currentTarget
    let wish = null
    try { wish = JSON.parse(row.dataset.wish) } catch (_error) { wish = null }
    this.open(wish)
  }

  open(wish) {
    this.currentId = wish && wish.id ? String(wish.id) : null
    this.removing = false
    this.headingTarget.textContent = wish ? "Rediger ønske" : "Nytt ønske"
    this.wishIdTarget.value = this.currentId || ""
    this.destroyTarget.value = "0"
    this.nameTarget.value = (wish && wish.name) || ""
    this.priceTarget.value = (wish && wish.price) || ""
    this.setQty(wish && wish.amount)
    this.urlTarget.value = (wish && wish.url) || ""
    this.noteTarget.value = (wish && wish.note) || ""
    this.syncPhoto((wish && wish.photo_url) || "")
    this.deleteButtonTarget.hidden = !wish
    this.submitAddTarget.hidden = !!wish
    this.submitDoneTarget.hidden = !wish
    this.nameInput()
    this.dialogTarget.showModal()
    this.nameTarget.focus()
  }

  close(event) {
    if (event) event.preventDefault()
    this.dialogTarget.close()
  }

  // Any way the modal closes (X, Escape, backdrop): if a background save turned
  // this into a real list meanwhile, go to its proper editor page now.
  closed() {
    if (this.pendingVisit) this.visit(this.pendingVisit)
  }

  // A click on the <dialog> element itself (not its children) is the backdrop.
  backdrop(event) {
    if (event.target === this.dialogTarget) this.close()
  }

  // The submit button wakes up once the wish has a name.
  nameInput() {
    this.submitTarget.disabled = this.nameTarget.value.trim().length === 0
  }

  // Mirror the title (a new list is created along with its first wish; an
  // unsaved title edit rides along) and tell the server what to do afterwards.
  submitting(event) {
    if (this.inFlight) return event.preventDefault()
    // A background title save may still be creating the list; wait for it so
    // the wish lands on that list rather than creating a second one.
    if (this.titleSave) {
      event.preventDefault()
      this.titleSave.then(() => this.wishFormTarget.requestSubmit())
      return
    }
    this.inFlight = true
    this.pendingVisit = null
    this.headerMirrorTarget.value = this.titleTarget.value
    this.afterTarget.value = this.removing ? "stay" : (this.currentId ? `focus:${this.currentId}` : "added")
    this.setStatus("Lagrer…")
  }

  // Product image: hand the URL to the modal's link-preview controller so its
  // thumbnail + hidden photo_url field agree with the wish being edited.
  syncPhoto(url) {
    const preview = this.application.getControllerForElementAndIdentifier(this.wishFormTarget, "link-preview")
    if (preview) {
      url ? preview.show(url) : preview.clear()
    } else if (this.hasPhotoTarget) {
      this.photoTarget.value = url
    }
  }

  // --- quantity stepper ---------------------------------------------------
  // Blank stays blank ("untracked single"); only an explicit count is stored.
  setQty(value) {
    const n = parseInt(value, 10)
    this.qtyTarget.value = n > 0 ? String(n) : ""
    this.qtyDisplayTarget.textContent = n > 0 ? n : 1
  }

  incQty(event) { this.stepQty(event, +1) }
  decQty(event) { this.stepQty(event, -1) }

  stepQty(event, delta) {
    if (event) event.preventDefault()
    const current = parseInt(this.qtyTarget.value, 10) || 1
    this.setQty(Math.max(1, current + delta))
  }

  // --- removing -----------------------------------------------------------
  // Deleting goes through a confirmation modal stacked on top of the editor.
  remove(event) {
    if (event) event.preventDefault()
    const name = this.nameTarget.value.trim() || "Uten navn"
    this.confirmNameTarget.textContent = `«${name}»`
    this.confirmDialogTarget.showModal()
  }

  // "Slett" in the confirmation modal: persist the deletion right away.
  confirmRemove(event) {
    if (event) event.preventDefault()
    this.confirmDialogTarget.close()
    if (!this.currentId || this.inFlight) return
    this.removing = true
    this.destroyTarget.value = "1"
    this.wishFormTarget.requestSubmit()
  }

  // "Avbryt" / backdrop / Escape: keep the wish, back to the editor.
  cancelRemove(event) {
    if (event) event.preventDefault()
    this.confirmDialogTarget.close()
  }

  confirmBackdrop(event) {
    if (event.target === this.confirmDialogTarget) this.cancelRemove()
  }

  // --- helpers ------------------------------------------------------------
  csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.content : ""
  }
}
